import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Language } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';
import {
  ActiveDirectoryConfiguration,
  UtilityInformation,
  UtilityLanguageSuffix,
  UtilityLoginData,
} from '../../models/utility.model';
import { PermissionsService } from '../../services/permissions.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilitySharedService } from '../../services/shared//utility-shared.service';
import { OtpVerificationComponent } from './otp-verification/otp-verification.component';

@Component({
  selector: 'lib-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  public email: any;
  public pass: any;
  public showPw: boolean = false;
  stepsDone: number = 0;
  token: string = '';
  urlEmail: string = '';
  urlPassword: string = '';
  urlUtilityId: string = '';
  utilityLoginData: UtilityLoginData = new UtilityLoginData();
  backgroundImageUrl: string = 'assets/login-background.jpg';
  logoImageUrl: string = '../../../assets/logo-login.svg';
  initLogin: boolean = false;

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    public permissionsService: PermissionsService,
    // private reportStateService: ReportStateService,
    private translateService: TranslateService,
    private utilitySharedService: UtilitySharedService,
  ) {}

  ngOnInit(): void {
    this.authService.getUtilityLoginData().subscribe({
      next: (response) => {
        this.utilityLoginData = response.body.data;
        this.backgroundImageUrl =
          this.utilityLoginData?.utilityInformation?.backgroundImageUrl ||
          'assets/login-background.jpg';
        this.logoImageUrl =
          this.utilityLoginData?.utilityInformation?.logoImageUrl ??
          '../../../assets/logo-login.svg';
        this.initLogin = true;
      },
      error: () => {
        this.toastr.error('Error fetching Active Directory Info.');
      },
    });

    this.urlEmail = this.route.snapshot.queryParams['email'];
    this.urlPassword = this.route.snapshot.queryParams['p'];
    this.urlUtilityId = this.route.snapshot.queryParams['utilityId'];
    this.token = this.route.snapshot.queryParams['token'];
    if (this.token) {
      this.authService.logout(false);
      this.tokenLogin();
    } else if (this.urlEmail && this.urlPassword && this.urlUtilityId) {
      this.authService
        .bpsLogin(this.urlEmail, this.urlPassword, this.urlUtilityId)
        .subscribe(
          (loginResponse) => {
            this.loginCommon(loginResponse);
          },
          (error) => {
            this.toastr.error('An error occurred.');
          },
        );
    } else if (this.authService.isLoggedIn()) {
      if (this.permissionsService.checkIfhasUtility()) {
        this.permissionsService.isMapViewAllowed &&
        this.permissionsService.hasDashboardConfigMap
          ? this.router.navigate(['/map'])
          : this.router.navigate(['/dashboard']);
      } else {
        this.router.navigate(['/dashboard-bps']);
      }
    }
  }

  async loginMicrosoft() {
    try {
      const { PublicClientApplication } = await import('@azure/msal-browser');
      const msalInstance = new PublicClientApplication({
        auth: {
          clientId:
            this.utilityLoginData.activeDirectoryConfiguration?.applicationId,
          authority:
            'https://login.microsoftonline.com/' +
            this.utilityLoginData.activeDirectoryConfiguration?.tenantId,
          redirectUri: window.location.origin,
        },
      });
      await msalInstance.initialize();
      const result = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email'],
      });
      const email =
        result.account?.username ??
        ((result.idTokenClaims as any)?.['preferred_username'] as string);
      this.authService.login(email, undefined, result.idToken).subscribe({
        next: (loginResponse) => this.loginCommon(loginResponse),
        error: (err) => {
          this.toastr.error('Microsoft login failed.');
        },
      });
    } catch (err: any) {
      const cancelCodes = ['user_cancelled', 'popup_window_closed'];
      if (!cancelCodes.includes(err?.errorCode)) {
        this.toastr.error('Microsoft login failed.');
      }
    }
  }

  tokenLogin() {
    this.authService.tokenLogin(this.token).subscribe(
      (loginResponse) => {
        this.loginCommon(loginResponse);
      },
      (error) => {
        this.toastr.error('An error ocurred.');
      },
    );
  }

  loginCommon(x: any) {
    if (
      (x.status == 'OK' || x.status === '200') &&
      x.message === 'Email Sent.'
    ) {
      this.openOTPModal(this.email, this.pass);
      return;
    }
    if (x.status === 'OK' || x.status === '200') {
      localStorage.setItem('token', x.data.token);
      localStorage.setItem('translation', JSON.stringify(x.data.translation));

      const user: any = this.authService.decodeJWTToken();
      if (user) {
        localStorage.setItem('preferredLanguage', user.preferredLanguage);
        this.utilitySharedService.setUtilityId(
          user.utilityId ? user.utilityId : 0,
        );
        localStorage.setItem('isAdmin', '1');
        localStorage.setItem(
          'bpsAdminLevel',
          user.bpsAdminLevel ? user.bpsAdminLevel : 0,
        );
        if (this.permissionsService.checkIfhasUtility()) {
          if (x.data?.utility?.dashboardConfiguration) {
            localStorage.setItem(
              'configuration',
              JSON.stringify(x.data.utility.dashboardConfiguration),
            );
          }

          localStorage.setItem(
            'defaultCaimsCodePatterns',
            JSON.stringify(x.data?.utility?.defaultCaimsCodePatterns ?? true),
          );

          if (x.data?.utility?.validationRules) {
            localStorage.setItem(
              'validationRules',
              JSON.stringify(x.data.utility.validationRules),
            );
          }

          if (x.data?.utility?.colorCode) {
            localStorage.setItem('colorCode', x.data.utility.colorCode);
            this.setColors(x.data.utility.colorCode);
          }

          if (x.data?.utility?.imageUrl) {
            localStorage.setItem('logoUrl', x.data.utility.imageUrl);
          }
          if (x.data?.utility?.currency) {
            localStorage.setItem('currency', x.data.utility.currency);
          }
          if (x.data?.utility?.longitude) {
            localStorage.setItem('longitude', x.data.utility.longitude);
          }

          if (x.data?.utility?.latitude) {
            localStorage.setItem('latitude', x.data.utility.latitude);
          }

          if (x.data?.utility?.mapConfiguration) {
            localStorage.setItem(
              'mapConfiguration',
              JSON.stringify(x.data.utility.mapConfiguration),
            );
          }

          if (x.data?.utility?.aiMapConfiguration) {
            localStorage.setItem(
              'aiMapConfiguration',
              JSON.stringify(x.data.utility.aiMapConfiguration),
            );
          }

          if (x.data?.utility?.accountNumberAndSpnRules) {
            localStorage.setItem(
              'accountNumberAndSpnRules',
              JSON.stringify(x.data.utility.accountNumberAndSpnRules),
            );
          }

          localStorage.setItem(
            'languageSuffixes',
            JSON.stringify(x.data?.utility?.languageSuffixes ?? []),
          );

          this.setLanguage();

          this.permissionsService.getPermissions().subscribe({
            next: () => {
              this.permissionsService.isMapViewAllowed &&
              this.permissionsService.hasDashboardConfigMap
                ? this.router.navigate(['map'])
                : this.router.navigate(['dashboard']);
            },
            error: () => this.toastr.error('Permissions load failed'),
          });

          let notVisibleFields: string[] =
            x.data.utility.notVisibleFields || [];

          if (
            x.data?.utility?.notVisibleFields &&
            x.data?.utility?.notVisibleFields !== 'undefined'
          ) {
            localStorage.setItem(
              'notVisibleFields',
              JSON.stringify(notVisibleFields),
            );
          }
        } //bps admin user
        else {
          localStorage.setItem('longitude', '0.381388470567412');
          localStorage.setItem('latitude', '5.906986824806585');
          this.permissionsService.getPermissions().subscribe({
            next: () => {
              this.setBpsAdminColors();
              this.router.navigate(['dashboard-bps']);
            },
            error: () => this.toastr.error('Permissions load failed'),
          });
        }
      }
    } else this.toastr.error(x.message);
  }

  login() {
    this.authService.login(this.email, this.pass).subscribe({
      next: (loginResponse) => {
        this.utilitySharedService.removeUtilityId();
        this.loginCommon(loginResponse);
      },
      error: () => {
        this.toastr.error('An error occurred.');
      },
    });
  }

  setBpsAdminColors() {
    document.documentElement.style.setProperty('--primary-color', '#03423E');
    document.documentElement.style.setProperty('--bg-color', '#F5FBFA');
    document.documentElement.style.setProperty(
      '--table-root-bg-color',
      '#EBF1F1',
    );
    document.documentElement.style.setProperty('--card-colr', '#e4ecea');
    document.documentElement.style.setProperty('--bubble-color', '#688F8D');
  }

  setColors(color: string) {
    document.documentElement.style.setProperty('--primary-color', color);
    document.documentElement.style.setProperty('--card-color', color + '22');
    document.documentElement.style.setProperty('--bg-color', color + '08');
    document.documentElement.style.setProperty('--bubble-color', color + '95');
    document.documentElement.style.setProperty('--bubble-color', color + '95');
  }

  showPassword() {
    this.showPw = !this.showPw;
  }

  checkIfLocalStorageReady() {
    this.stepsDone++;
    if (this.stepsDone == 2) {
      this.stepsDone = 0;
      this.router.navigate(['map']);
    }
  }

  setLanguage() {
    const langIndex = parseInt(
      localStorage.getItem('preferredLanguage') ?? '0',
      10,
    );
    const suffixes = this.getLanguageSuffixes();

    const translationFileName = this.getTranslationFileName(
      langIndex,
      suffixes,
    );
    this.translateService.setDefaultLang('en');
    this.translateService.use(translationFileName);
  }

  getLanguageSuffixes(): UtilityLanguageSuffix[] {
    return JSON.parse(localStorage.getItem('languageSuffixes') ?? '[]');
  }
  getTranslationFileName(
    langIndex: number,
    suffixes: UtilityLanguageSuffix[],
  ): string {
    const fileName = langIndex === Language.English ? 'en' : 'fr';
    const suffix = suffixes.find((x) => x.language === langIndex)?.suffix || '';
    return `${fileName}${suffix}`;
  }

  openOTPModal(email: string, pass: string) {
    this.router.navigate(['/verify-otp'], {
      state: { email: email, password: pass },
      replaceUrl: true,
    });
  }
}
